import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User } from "@/types";
import { authApi } from "@/services/authService";

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: () => void;
  logout: () => void;
  isLoading: boolean;
  isAdmin: boolean;
  /** OKR read gate: leaders, directors, CEO and admins. Mirrors BE `OKR_LEADER_ONLY`. */
  canViewOkr: boolean;
  /** OKR write gate: director, CEO and admins. Mirrors BE `OKR_DIRECTOR_ONLY`. */
  canEditOkr: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const checkAuth = async () => {
    try {
      const userData = await authApi.getProfile();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAuth();
      }
    };

    window.addEventListener("focus", checkAuth);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", checkAuth);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const login = () => {
    authApi.initiateGoogleLogin();
  };

  const logout = async () => {
    try {
      await authApi.logout();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      // Even if logout fails on server, clear local state
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const isAdmin = user?.role?.name === "admin";
  const canEditOkr = isAdmin || user?.seniorityLevel === "director" || user?.seniorityLevel === "ceo";
  const canViewOkr = canEditOkr || user?.isLeader === true;

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, isLoading, isAdmin, canViewOkr, canEditOkr }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
